import {
  MapAreaService,
  MapAreaRenderer,
  SelectionRenderService,
  clearAllCanvases,
  isValidContext
} from '@/modules/map-editor';
import type { CanvasContexts, RenderDataGetters, RenderData } from '@/modules/map-editor';
import type { Node, Edge } from '@/modules/map-editor';
import type { MapAreaState, MapImageConfig } from '@/modules/map-editor';

/**
 * Service for managing map rendering.
 * Orchestrates rendering of map area, nodes, edges, and selections.
 */
export class MapRenderService {
  private contexts: CanvasContexts;
  private mapAreaService: MapAreaService;
  private mapAreaRenderer: MapAreaRenderer;
  private selectionRenderer: SelectionRenderService;

  constructor(
    contexts: CanvasContexts,
    mapAreaService: MapAreaService
  ) {
    this.contexts = contexts;
    this.mapAreaService = mapAreaService;
    this.mapAreaRenderer = new MapAreaRenderer();
    this.selectionRenderer = new SelectionRenderService();
  }

  private dataGetters: RenderDataGetters | null = null;

  /**
   * Register closures that supply all render data.
   * Called once during component setup — the animation loop and render() read through these.
   */
  setDataGetters(getters: RenderDataGetters): void {
    this.dataGetters = getters;
  }

  private getData(): RenderData {
    if (!this.dataGetters) {
      throw new Error('Data getters not set. Call setDataGetters first.');
    }

    return {
      nodes: this.dataGetters.nodes(),
      edges: this.dataGetters.edges(),
      layerId: this.dataGetters.layerId(),
      selectedEdgeId: this.dataGetters.selectedEdgeId(),
      selectedNodes: this.dataGetters.selectedNodes(),
      mapData: this.dataGetters.mapData(),
      zoomLevel: this.dataGetters.zoomLevel(),
      selectionBox: this.dataGetters.selectionBox(),
      isHoveringSelectionBox: this.dataGetters.isHoveringSelectionBox()
    };
  }

  /**
   * Draw map background (area + image)
   */
  private drawMapBackground(
    ctx: CanvasRenderingContext2D,
    zoomLevel: number,
    mapData: MapImageConfig
  ): void {
    const mapAreaState = this.mapAreaService.getState();

    // Draw map area rectangle
    this.mapAreaRenderer.drawMapArea(ctx, zoomLevel, mapAreaState);

    // Draw background image
    this.mapAreaRenderer.drawMapImage(ctx, zoomLevel, mapAreaState, mapData);
  }

  private drawNodes(
    nodes: Node[],
    zoomLevel: number
  ): void {
    for (let i = 0; i < nodes.length; i++) {
      nodes[i].draw(this.contexts, zoomLevel);
    }
  }

  private drawEdges(
    edges: Edge[],
    zoomLevel: number,
    selectedEdgeId: number | null
  ): void {
    for (let i = 0; i < edges.length; i++) {
      edges[i].draw(this.contexts, zoomLevel, selectedEdgeId);
    }
  }

  /**
   * Draw selection highlights on the highlights canvas layer.
   */
  private drawSelection(
    selectedNodes: Node[],
    zoomLevel: number,
    selectionBox: import('@/modules/map-editor').SelectionBox | null = null,
    isHoveringSelectionBox = false
  ): void {
    if (isValidContext(this.contexts.ctxHighlights)) {
      const mapAreaState = this.mapAreaService.getState();

      // Draw element selection highlights
      if (selectedNodes.length > 0) {
        this.selectionRenderer.render({
          elements: selectedNodes,
          ctx: this.contexts.ctxHighlights,
          mapAreaX: mapAreaState.x,
          mapAreaY: mapAreaState.y,
          zoomLevel,
          hovered: isHoveringSelectionBox
        });
      }

      // Draw lasso rectangle if active
      if (selectionBox) {
        this.selectionRenderer.renderLassoRect(this.contexts.ctxHighlights, selectionBox);
      }
    }
  }

  private buildNodeMap(nodes: Node[]): Map<number, Node> {
    const map = new Map<number, Node>();
    for (let i = 0; i < nodes.length; i++) {
      map.set(nodes[i].id, nodes[i]);
    }
    return map;
  }

  private buildEdgeMap(edges: Edge[]): Map<number, Edge> {
    const map = new Map<number, Edge>();
    for (let i = 0; i < edges.length; i++) {
      map.set(edges[i].id, edges[i]);
    }
    return map;
  }

  private updateNodeCaches(nodeIds: number[]): void {
    const { nodes, layerId } = this.getData();
    const nodeMap = this.buildNodeMap(nodes);

    for (const nodeId of nodeIds) {
      const id = typeof nodeId === 'string' ? parseInt(nodeId) : nodeId;
      const node = nodeMap.get(id);
      if (!node) continue;

      node.cacheInstanceSetDimensions();
      node.cache(layerId);
    }
  }

  private updateNodeMediaCaches(nodeIds: number[]): void {
    const { nodes, layerId } = this.getData();
    const nodeMap = this.buildNodeMap(nodes);

    for (const nodeId of nodeIds) {
      const id = typeof nodeId === 'string' ? parseInt(nodeId) : nodeId;
      const node = nodeMap.get(id);
      if (!node) continue;

      node.cacheUpdate(layerId);
    }
  }

  private updateEdgeCaches(edgeIds: number[]): void {
    const { edges, layerId } = this.getData();
    const edgeMap = this.buildEdgeMap(edges);

    for (const edgeId of edgeIds) {
      const id = typeof edgeId === 'string' ? parseInt(edgeId) : edgeId;
      const edge = edgeMap.get(id);
      if (!edge) continue;

      edge.cacheInstanceSetDimensions();
      edge.cache(layerId, null);
    }
  }

  /**
   * Execute a single animation frame: check dirty flags, invalidate caches, render.
   * Called by the animation loop each frame.
   *
   * @param mapAreaState - Map area state with update flags and dirty element sets
   * @returns true if a render occurred
   */
  frameUpdate(mapAreaState: MapAreaState): boolean {
    let needsRender = false;

    // Continuous animation (zoom/pan)
    if (mapAreaState.update) {
      needsRender = true;
    }

    // Discrete element changes
    if (mapAreaState.updateElement) {
      if (mapAreaState.nodesToUpdate.size) {
        const nodeIds = Array.from(mapAreaState.nodesToUpdate);
        mapAreaState.nodesToUpdate.clear();
        // Remove from mediaOnly set — full update is a superset
        for (const id of nodeIds) mapAreaState.mediaOnlyNodes.delete(id);
        this.updateNodeCaches(nodeIds);
      }

      if (mapAreaState.mediaOnlyNodes.size) {
        const mediaNodeIds = Array.from(mapAreaState.mediaOnlyNodes);
        mapAreaState.mediaOnlyNodes.clear();
        this.updateNodeMediaCaches(mediaNodeIds);
      }

      if (mapAreaState.edgesToUpdate.size) {
        const edgeIds = Array.from(mapAreaState.edgesToUpdate);
        mapAreaState.edgesToUpdate.clear();
        this.updateEdgeCaches(edgeIds);
      }

      needsRender = true;
      mapAreaState.updateElement = false;
    }

    if (needsRender) {
      this.render();
    }

    return needsRender;
  }

  /**
   * Render a complete frame using data from the registered getters.
   */
  private render(): void {
    // Validate buffered context
    if (!isValidContext(this.contexts.ctxBuffered)) {
      console.error('Buffered canvas context is not available');
      return;
    }

    // Check if buffered canvas has valid dimensions
    if (this.contexts.ctxBuffered.canvas.width === 0 || this.contexts.ctxBuffered.canvas.height === 0) {
      console.warn('Buffered canvas has zero dimensions, skipping render');
      return;
    }

    const { nodes, edges, mapData, selectedEdgeId, selectedNodes, zoomLevel, selectionBox, isHoveringSelectionBox } = this.getData();

    // Clear all canvases
    clearAllCanvases(this.contexts);

    // Draw map background (area + image)
    this.drawMapBackground(this.contexts.ctxBuffered, zoomLevel, mapData);

    // Draw edges (draw before nodes to appear behind them)
    this.drawEdges(edges, zoomLevel, selectedEdgeId);

    // Draw nodes
    this.drawNodes(nodes, zoomLevel);

    // Draw selection highlights and lasso rectangle
    this.drawSelection(selectedNodes, zoomLevel, selectionBox, isHoveringSelectionBox);

    // Transfer buffered canvas to main canvas
    this.transferToMainCanvas();
  }

  /**
   * Transfer buffered canvas to main canvas
   */
  private transferToMainCanvas(): void {
    if (
      isValidContext(this.contexts.ctx) &&
      isValidContext(this.contexts.ctxBuffered)
    ) {
      this.contexts.ctx.drawImage(
        this.contexts.ctxBuffered.canvas,
        0,
        0
      );
    }
  }

}